<template>
  <div
    :class="[block.bg_color ? `--bg-${block.bg_color}` : null, 'block']"
    :key="block.uuid"
    :data-block="block.uuid"
    :data-role="ROLES.BLOCK"
    :data-type="block.type"
  >
    <template v-if="block.type === BLOCK_TYPES.HERO_BLOCK">
      <hero
        :block-action="block.data.block_action"
        :video-finished-action="block.data.video_finished_action"
        :image="blockImage(block)"
        :video="blockVideo(block)"
        :audio="blockAudio(block)"
        :video-options="{
          playsInline: block.data.video_plays_inline,
          autoInitialize: true,
          portrait: block.data.video_is_portrait,
          cover: block.data.video_fit === 'cover'
        }"
        :overlay-shadow="block.data.is_overlaid"
        :image-opacity-ratio="parseIntIfGiven(block.data.image_opacity_ratio)"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :styleOptions="{
          size: block.data.background_size,
          heightRatio: block.data.height_ratio,
          heightRatioUnit: block.data.height_ratio_unit,
          borderRadiusRatio: block.data.border_radius_ratio,
          textPositionX: block.data.text_position_x,
          textPositionY: block.data.text_position_y
        }"
      >
        <template v-slot="{ height }">
          <blocks-container
            :style="`min-height: ${height}`"
            class="--light-mode"
            :blocks="block.blocks"
            :page-id="pageId"
            :parent-block="block"
            :unlock="unlock"
            :ignore-blocks="ignoreBlocks"
          />
        </template>
      </hero>
    </template>
    <template v-if="block.type === BLOCK_TYPES.ATTACHMENT_BLOCK">
      <attachment-block
        :page-id="pageId"
        :image="blockImage(block)"
        :attachment="blockAttachment(block)"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :height-ratio="parseIntIfGiven(block.data.height_ratio)"
        :card-style="block.data.card_style"
        :unlock="unlock"
      >
        <template v-slot="{ height }">
          <blocks-container
            :style="`min-height: ${height}`"
            :blocks="block.blocks"
            :page-id="pageId"
            :parent-block="block"
            :unlock="unlock"
            :ignore-blocks="ignoreBlocks"
          />
        </template>
      </attachment-block>
    </template>

    <template v-if="block.type === BLOCK_TYPES.IFRAME_BLOCK">
      <iframe-block
        :url="block.data.url"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :height-ratio="parseIntIfGiven(block.data.height_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.TEXT_BLOCK">
      <text-block
        :uuid="block.uuid"
        :content="block.data.content"
        :page-attribute="block.data.page_attribute"
        :dynamic-content="block.data.dynamic_content"
        :size-ratio="parseIntIfGiven(block.data.size_ratio)"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :tag="block.data.tag"
        :align="block.data.align"
        :color="block.data.color"
        :page-id="pageId"
        :collapsable="block.data.is_collapsable"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.COLLECTION_BLOCK">
      <collection
        :path="block.data.collection ? block.data.collection.path : null"
        :items="blockItems(block)"
        :card-style="block.data.card_style"
        :card-display="block.data.card_display"
        :direction="block.data.direction"
        :columns="parseIntIfGiven(block.data.columns)"
        :rows="parseIntIfGiven(block.data.rows)"
        :column-width-ratio="parseIntIfGiven(block.data.column_width_ratio)"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :card-image-size="parseIntIfGiven(block.data.image_size_ratio)"
        :modifiers="block.data.image_shown ? null : 'no-image'"
        :show-price="block.data.show_price"
        :show-time="block.data.show_time"
        :show-booked="block.data.show_booked"
        :unlock="unlock"
        :collection="block.data.collection"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.BUTTON_BLOCK">
      <button-block
        :block-action="block.data.block_action"
        :text="block.data.text"
        :post-purchase-text="block.data.post_purchase_text"
        :display="block.data.display"
        :size="block.data.size"
        :btn-style="block.data.style"
        :variant="block.data.variant"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.CHAT_BLOCK">
      <chat-block
        :message-thread-id="block.data.message_thread_id"
        :is-comment-thread="block.data.is_comment_thread"
        :is-feed="block.data.is_feed"
        :page-id="pageId"
        :channel-id="block.data.channel_id"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.LIVESTREAM_BLOCK">
      <livestream-block
        :id="block.data.livestream.id"
        :provider="block.data.livestream.provider"
        :remote-id="block.data.livestream.remote_id"
        :message-thread-id="block.data.livestream.message_thread_id"
        :video="block.data.livestream.video"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.AVATAR_BLOCK">
      <avatar-block />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.IMAGE_BLOCK">
      <image-block
        :image="block.data.image"
        :align="block.data.align"
        :width-ratio="parseIntIfGiven(block.data.width_ratio)"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.SHARE_BLOCK">
      <share-block
        :url="block.data.url"
        :title="block.data.title"
        :visible-platforms="Object.keys(block.data).filter(n => block.data[n])"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.WORKOUT_BLOCK">
      <workout-block
        :invert-colors="block.bg_color === BLOCK_COLORS.BLACK"
        :page-id="pageId"
        :workout="block.data.workout"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.ICON_LIST_BLOCK">
      <icon-list-block
        :icons="block.data.icon_list"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :text-color="block.data.text_color"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.MAP_BLOCK">
      <map-block
        :map="block.data"
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
        :height-ratio="parseIntIfGiven(block.data.height_ratio)"
      />
    </template>

    <template v-else-if="block.type === BLOCK_TYPES.COMPOSED_BLOCK">
      <composed-block
        :space-x-ratio="parseIntIfGiven(block.space_x_ratio)"
        :space-top-ratio="parseIntIfGiven(block.space_top_ratio)"
        :space-bottom-ratio="parseIntIfGiven(block.space_bottom_ratio)"
      >
        <template v-slot="{ padding }">
          <blocks-container
            :style="{ padding }"
            :blocks="block.blocks"
            :page-id="pageId"
            :parent-block="block"
            :unlock="unlock"
            :ignore-blocks="ignoreBlocks"
          />
        </template>
      </composed-block>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"

import {
  buildBlockMountedEvent,
  getBlockImage,
  getBlockVideo,
  getBlockAudio,
  getBlockItems,
  BLOCK_TYPES,
  ROLES,
  BLOCK_COLORS,
  BACKGROUND_COLORS
} from "@/lib/stream-utils"
import { isPresent } from "@/lib/utils"
import EventBus, { EVENTS } from "@/lib/event-bus"

import Hero from "@/components/Hero"
import TextBlock from "@/components/TextBlock"
import Collection from "@/components/Collection"
import ButtonBlock from "@/components/ButtonBlock"

export default {
  components: {
    Hero,
    TextBlock,
    Collection,
    ButtonBlock,
    BlocksContainer: () => import("@/components/stream/BlocksContainer"),
    AvatarBlock: () => import("@/components/AvatarBlock"),
    AttachmentBlock: () => import("@/components/AttachmentBlock"),
    IframeBlock: () => import("@/components/blocks/IframeBlock"),
    ChatBlock: () => import("@/components/blocks/ChatBlock"),
    LivestreamBlock: () => import("@/components/blocks/LivestreamBlock"),
    ImageBlock: () => import("@/components/blocks/ImageBlock"),
    ShareBlock: () => import("@/components/blocks/ShareBlock"),
    WorkoutBlock: () => import("@/components/blocks/WorkoutBlock"),
    IconListBlock: () => import("@/components/blocks/IconListBlock"),
    MapBlock: () => import("@/components/blocks/MapBlock"),
    ComposedBlock: () => import("@/components/blocks/ComposedBlock")
  },

  props: {
    block: { type: Object, required: true },
    parentBlock: { type: Object, default: () => ({}) },
    index: { type: Number, default: 1 },
    unlock: { type: Boolean, default: null },
    pageId: { type: [String, Number], default: null },
    ignoreBlocks: { type: Array, default: () => [] }
  },

  data() {
    return {
      BLOCK_TYPES,
      ROLES,
      BLOCK_COLORS,
      BACKGROUND_COLORS
    }
  },

  computed: {
    ...mapState("pages", ["pageData"]),
    ...mapGetters("pages", ["getPageAttributeValues"])
  },

  mounted() {
    // TODO: Lazy load if in builder?
    EventBus.$emit(
      EVENTS.BLOCK_MOUNTED,
      buildBlockMountedEvent(() => this.block, this.$el)
    )
  },

  methods: {
    isPresent,

    blockImage(block) {
      return getBlockImage(block, this.getPageAttributeValues(this.pageId))
    },

    blockVideo(block) {
      return getBlockVideo(block, this.getPageAttributeValues(this.pageId))
    },

    blockAudio(block) {
      return getBlockAudio(block, this.getPageAttributeValues(this.pageId))
    },

    blockAttachment(block) {
      return isPresent(block.data.attachment)
        ? block.data.attachment
        : undefined
    },

    blockItems(block) {
      return getBlockItems(block, this.getPageAttributeValues(this.pageId))
    },

    parseIntIfGiven(value) {
      return value || value === 0 ? parseInt(value) : undefined
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixins";

.block {
  position: relative;

  &.--bg-white {
    @include bg-white;
  }

  &.--bg-black {
    @include bg-black;
  }

  &.--bg-light_grey {
    @include bg-light-grey;
  }

  &.--bg-grey {
    @include bg-dark-grey;
  }
}
</style>
